import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, CheckCheck, MessageCircle, FileText } from 'lucide-react';
import { api, Notification as AppNotification } from '@/lib/api';

const POLL_INTERVAL = 30_000; // 30 seconds

function setAppBadge(count: number) {
  const nav = navigator as Navigator & {
    setAppBadge?: (count?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  if (count > 0 && nav.setAppBadge) {
    nav.setAppBadge(count).catch(() => {});
    return;
  }
  if (count <= 0 && nav.clearAppBadge) {
    nav.clearAppBadge().catch(() => {});
  }
}

async function hasPushSubscription(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

async function showSystemNotification(diff: number) {
  if (localStorage.getItem('apoo_desktop_notifs') !== 'true') return;
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  // If the user has an active VAPID push subscription, the server sends
  // rich push notifications directly — skip the client-side fallback to
  // avoid duplicate popups.
  if (await hasPushSubscription()) return;

  // Fetch the latest unread notifications so we can show real content
  try {
    const res = await api.notifications.list({ unread: 'true', per_page: String(diff) });
    const items = res.data || [];

    const reg = 'serviceWorker' in navigator
      ? await navigator.serviceWorker.getRegistration()
      : null;

    for (const n of items) {
      const actor = n.actor_name || 'Someone';
      const title = n.type === 'new_topic'
        ? `${actor} started a new topic`
        : `${actor} replied`;
      const body = n.topic_title || n.message || '';
      const url = n.topic_id && n.topic_slug
        ? `/t/${n.topic_id}-${n.topic_slug}`
        : n.topic_id ? `/t/${n.topic_id}` : '/';
      const tag = `apoo-notif-${n.id}`;

      if (reg) {
        await reg.showNotification(title, {
          body,
          icon: '/icons/icon-192.png',
          badge: '/icons/icon-192.png',
          tag,
          data: { url },
        });
      } else {
        new Notification(title, { body, icon: '/icons/icon-192.png', tag });
      }
    }
  } catch {
    // Fallback: generic notification if the fetch fails
    const title = 'New forum notification';
    const body = `You have ${diff} new notification${diff > 1 ? 's' : ''}`;
    if ('serviceWorker' in navigator) {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          await reg.showNotification(title, {
            body, icon: '/icons/icon-192.png', badge: '/icons/icon-192.png',
            tag: 'apoo-notif', data: { url: '/' },
          });
          return;
        }
      } catch { /* fall through */ }
    }
    new Notification(title, { body, icon: '/icons/icon-192.png', tag: 'apoo-notif' });
  }
}

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  const fetchCount = useCallback(() => {
    api.notifications.unreadCount()
      .then(res => {
        const newCount = res.count;
        if (newCount > prevCountRef.current && prevCountRef.current >= 0) {
          const diff = newCount - prevCountRef.current;
          showSystemNotification(diff);
          // Vibrate on mobile
          if ('vibrate' in navigator) {
            try { navigator.vibrate(200); } catch {}
          }
        }
        prevCountRef.current = newCount;
        setUnreadCount(newCount);
        setAppBadge(newCount);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, POLL_INTERVAL);

    // Refresh immediately when the tab/PWA becomes visible again (critical for iOS)
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') fetchCount();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Listen for messages from the service worker (push arrived)
    function onSWMessage(event: MessageEvent) {
      if (event.data?.type === 'PUSH_RECEIVED') fetchCount();
    }
    navigator.serviceWorker?.addEventListener('message', onSWMessage);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      navigator.serviceWorker?.removeEventListener('message', onSWMessage);
    };
  }, [fetchCount]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function toggleDropdown() {
    if (!open) {
      setLoading(true);
      api.notifications.list({ per_page: '15' })
        .then(res => setNotifications(res.data || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
    setOpen(!open);
  }

  async function markRead(id: string) {
    await api.notifications.markRead(id).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(prev => {
      const next = Math.max(0, prev - 1);
      setAppBadge(next);
      return next;
    });
  }

  async function markAllRead() {
    await api.notifications.markAllRead().catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
    setAppBadge(0);
  }

  function notifLink(n: AppNotification): string {
    if (n.topic_id && n.topic_slug) {
      return `/t/${n.topic_id}-${n.topic_slug}`;
    }
    if (n.topic_id) {
      return `/t/${n.topic_id}`;
    }
    return '/';
  }

  function notifIcon(type: string) {
    switch (type) {
      case 'reply': return <MessageCircle size={14} className="text-primary flex-shrink-0" />;
      case 'new_topic': return <FileText size={14} className="text-emerald-500 flex-shrink-0" />;
      default: return <Bell size={14} className="text-gray-400 flex-shrink-0" />;
    }
  }

  function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="relative p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Notifications"
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 sm:w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary-hover transition-colors"
                title="Mark all as read"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-50 dark:divide-gray-700/50">
            {loading && (
              <div className="p-6 text-center text-sm text-gray-400">Loading...</div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="p-6 text-center text-sm text-gray-400 dark:text-gray-500">No notifications yet</div>
            )}
            {!loading && notifications.map(n => (
              <Link
                key={n.id}
                to={notifLink(n)}
                onClick={() => { if (!n.is_read) markRead(n.id); setOpen(false); }}
                className={`flex items-start gap-2.5 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors no-underline ${
                  !n.is_read ? 'bg-primary/[0.04] dark:bg-primary/[0.08]' : ''
                }`}
              >
                {n.actor_avatar ? (
                  <img src={n.actor_avatar} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0 mt-0.5">
                    {(n.actor_name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {notifIcon(n.type)}
                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                      {n.actor_name || 'Someone'}
                    </span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{timeAgo(n.created_at)}</span>
                    {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">{n.message}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
